"use client";

import { useUrlModal } from "./use-url-modal";

interface UseUrlModalControlsOptions {
  modalId: string;
  paramName?: string;
  replace?: boolean;
}

/**
 * Hook to get modal control functions for a specific modal ID
 * Useful when you need to trigger a modal from outside the modal component
 * 
 * @example
 * ```tsx
 * const { openModal, getUrl } = useUrlModalControls({ modalId: "edit-profile" });
 * 
 * // Open modal programmatically
 * <Button onClick={openModal}>Edit Profile</Button>
 * 
 * // Share URL with modal open
 * <Button onClick={() => navigator.clipboard.writeText(getUrl())}>
 *   Share Edit Link
 * </Button>
 * ```
 */
export function useUrlModalControls({
  modalId,
  paramName = "modal",
  replace = false,
}: UseUrlModalControlsOptions) {
  const { onOpen, onClose, getUrl } = useUrlModal({
    modalId,
    paramName,
    replace,
  });

  return {
    openModal: onOpen,
    closeModal: onClose,
    getModalUrl: getUrl,
  };
}
